import { Alert, Stack, Text } from 'braid-design-system';
import type React from 'react';
import { Helmet } from 'react-helmet';
import { SmartTextLink } from 'scoobie';

import type { Breadcrumb } from './components/Breadcrumbs/Breadcrumbs.tsx';
import { RENDERABLE_PAGES, type RenderablePage } from './renderablePages.ts';
import { mdxDocuments, tsxComponents } from './site.map.ts';

import { MarkdownPage } from '#src/templates/MarkdownPage.tsx';

export {
  isGeneratedPage,
  RENDERABLE_PAGES,
  type FlatPage,
  type PageNavigation,
  type RenderablePage,
} from './renderablePages.ts';

const Banner = (page: {
  replacement: RenderablePage['replacement'];
  supersedes?: string;
  type: Exclude<RenderablePage['type'], 'reviewed' | 'standalone'>;
}): React.JSX.Element => {
  switch (page.type) {
    case 'archived':
      return (
        <Alert tone="caution">
          <Text>
            This content has been archived.{' '}
            {page.replacement ? (
              <>
                You may be looking for its{' '}
                <SmartTextLink href={page.replacement}>
                  latest revision
                </SmartTextLink>
                .
              </>
            ) : (
              'It may contain guidance that is no longer relevant.'
            )}
          </Text>
        </Alert>
      );

    case 'draft':
      return (
        <Alert tone="info">
          <Text>This content is still in draft.</Text>
        </Alert>
      );

    case 'unstable':
      return (
        <Alert tone="caution">
          <Stack space="medium">
            <Text>
              This content provides an early look at an upcoming extension to
              the SEEK API. You may use it to scope integration effort, but the
              features described are not fully implemented and may be adjusted
              ahead of a production release.
            </Text>

            <Text>
              Check with your SEEK contact before starting any build work
              described here
              {page.supersedes ? (
                <>
                  , or refer to our{' '}
                  <SmartTextLink href={page.supersedes}>
                    prior documentation
                  </SmartTextLink>{' '}
                  for in-flight or existing integrations
                </>
              ) : null}
              .
            </Text>
          </Stack>
        </Alert>
      );
  }
};

const BY_REPLACEMENT = Object.fromEntries(
  Object.entries(RENDERABLE_PAGES).map(([path, page]) => [
    page.replacement,
    path,
  ]),
);

const getBreadcrumbs = (page: { path?: string }): Breadcrumb[] | null => {
  if (page.path === undefined) {
    return null;
  }

  const currentPage = RENDERABLE_PAGES[page.path];
  if (currentPage === undefined) {
    return null;
  }

  return [
    ...(currentPage.parent ? (getBreadcrumbs(currentPage.parent) ?? []) : []),
    {
      title: currentPage.label,
      href: currentPage.path,
    },
  ];
};

export const renderPage = (page: RenderablePage) => {
  const currentPage = RENDERABLE_PAGES[page.path];
  const next = currentPage?.next;
  const prev = currentPage?.prev;
  const baseBreadcrumbs = getBreadcrumbs(page);
  const breadcrumbs = baseBreadcrumbs
    ? [{ title: 'Docs', href: '/' }, ...baseBreadcrumbs]
    : null;

  switch (page.type) {
    case 'archived':
    case 'draft':
    case 'reviewed':
    case 'unstable':
      const mdxDocument = mdxDocuments[page.path];

      if (mdxDocument === undefined) {
        throw Error(`${page.path} does not have an associated component`);
      }

      const { toc, document } = mdxDocument;

      const supersededPage = BY_REPLACEMENT[page.path];

      const banner =
        page.type === 'reviewed' ? null : (
          <Banner
            replacement={page.replacement}
            supersedes={supersededPage}
            type={page.type}
          />
        );

      return (
        <>
          {page.type === 'archived' || page.hidden ? (
            // Hidden pages are not meant to be user-findable.
            // Archived pages are user-findable, under an 'archived' menu section,
            // but offer limited value to users who would be stumbling upon them via Google.

            <Helmet>
              <meta name="robots" content="noindex" />
            </Helmet>
          ) : null}

          <MarkdownPage
            banner={banner}
            breadcrumbs={breadcrumbs}
            document={document}
            label={page.label}
            next={next}
            prev={prev}
            toc={toc}
          />
        </>
      );

    case 'standalone':
      const Component = tsxComponents[page.path];

      if (Component === undefined) {
        throw Error(`${page.path} does not have an associated component`);
      }

      return <Component next={next} prev={prev} breadcrumbs={breadcrumbs} />;
  }
};
